import { useEffect, useState } from "react";
import { getApiUrl } from "../api";
import { getCustomerRequestNumber } from "../utils/requestReferences";
import { getStoredToken } from "../auth";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";

const formatAmount = (amount, language = "en") => new Intl.NumberFormat(language === "ar" ? "ar-AE" : "en-AE", {
  style: "currency", currency: "AED", maximumFractionDigits: 2,
}).format(Number(amount) || 0);

export const getCustomerStatusKey = (request) => {
  const status = `${request.statusCode || ""} ${request.status || ""} ${request.status_ar || ""}`.toUpperCase();
  const hasNamedStatus = Boolean(status.trim());
  const payment = `${request.paymentMethod || ""} ${request.paymentStatus || ""}`.toUpperCase();
  const completed = /COMPLETED|CLOSED|ARCHIVED/.test(status) || (!hasNamedStatus && request.statusId === 4);
  const paymentFinished = /COLLECT|COMPLET|PAID|EXECUT|SUCCESS/.test(payment) || Boolean(request.paymentDate) || completed;
  if (/\bEXPIRED\b/.test(status) || /منتهي(?:ة)?(?: الصلاحية)?/.test(status)) return "expired";
  if (/REJECT/.test(status) || (!hasNamedStatus && request.statusId === 5)) return "rejected";
  if (/WAITING[_ ]APPLICANT|ACTION[_ ]REQUIRED/.test(status) || request.actionRequired?.length) return "action-required";
  if (Number(request.approvedAmount) > 0 && /CHEQUE|CHECK/.test(payment)) return paymentFinished ? "cheque-collected" : "cheque-ready";
  if (Number(request.approvedAmount) > 0 && /BANK|TRANSFER/.test(payment) && paymentFinished) return "bank-transfer-completed";
  if (completed) return "completed";
  if (/APPROV/.test(status) || (!hasNamedStatus && [21, 24].includes(request.statusId))) return "approved";
  if (/\bSUBMITTED\b/.test(status) || (!hasNamedStatus && request.statusId === 0)) return "submitted";
  if (/\bRECEIVED\b|INITIAL[_ ]VALIDATION/.test(status)) return "received";
  return "under-process";
};

export const getCustomerStatus = (request, isArabic) => {
  const labels = {
    submitted: ["Submitted", "تم تقديم الطلب"],
    received: ["Received", "تم استلام الطلب"],
    "under-process": ["Under Process", "قيد الإجراء"],
    "action-required": ["Action Required", "إجراء مطلوب من المتعامل"],
    approved: ["Approved", "تمت الموافقة"],
    expired: ["Expired", "منتهي الصلاحية"],
    rejected: ["Rejected", "مرفوض"],
    "cheque-ready": ["Cheque Ready", "الشيك جاهز"],
    "cheque-collected": ["Cheque Collected", "تم استلام الشيك"],
    "bank-transfer-completed": ["Bank Transfer Completed", "تم التحويل البنكي"],
    completed: ["Completed", "مكتمل"],
  };
  return labels[getCustomerStatusKey(request)][isArabic ? 1 : 0];
};

export const syncExpiredStatusFromDashboard = (request, dashboardRequests = []) => {
  if (!request || getCustomerStatusKey(request) === "expired") return request;
  const references = [request.requestno, request.customerRequestNumber, request.internalRequestNumber]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());
  const dashboardRequest = dashboardRequests.find((item) => [item.requestno, item.customerRequestNumber, item.internalRequestNumber]
    .filter(Boolean)
    .some((value) => references.includes(String(value).trim().toLowerCase())));
  return getCustomerStatusKey(dashboardRequest) === "expired" ? {
    ...request,
    statusCode: "EXPIRED",
    status: "Expired",
    status_ar: "منتهي الصلاحية",
  } : request;
};

export const buildTimeline = (request, isArabic) => {
  const activities = Array.isArray(request.timeline) ? request.timeline : [];
  const requirements = Array.isArray(request.actionRequired) ? request.actionRequired : [];
  const statusText = `${request.statusCode || ""} ${request.status || ""} ${request.status_ar || ""}`.toUpperCase();
  const hasNamedStatus = Boolean(statusText.trim());
  const isSubmitted = /\bSUBMITTED\b/.test(statusText) || (!hasNamedStatus && request.statusId === 0);
  const isReceivedOnly = /\bRECEIVED\b|INITIAL[_ ]VALIDATION/.test(statusText);
  const isActionRequired = /WAITING[_ ]APPLICANT|ACTION[_ ]REQUIRED/.test(statusText) || requirements.length > 0;
  const isExpired = /\bEXPIRED\b/.test(statusText) || /منتهي(?:ة)?(?: الصلاحية)?/.test(statusText);
  const isRejected = /REJECT/.test(statusText) || (!hasNamedStatus && request.statusId === 5);
  const isCompleted = /COMPLETED|CLOSED|ARCHIVED/.test(statusText) || (!hasNamedStatus && request.statusId === 4);
  const isApproved = !isRejected && (/APPROV/.test(statusText) || (!hasNamedStatus && [21, 24].includes(request.statusId)) || isCompleted || Number(request.approvedAmount) > 0);

  const activityText = (activity) => [
    activity.actionCode,
    activity.newStatusCode,
    activity.title_en,
    activity.title_ar,
    activity.remarks_en,
    activity.remarks_ar,
  ].filter(Boolean).join(" ").toUpperCase();
  const activityDate = (pattern, latest = false) => {
    const source = latest ? [...activities].reverse() : activities;
    const activity = source.find((item) => pattern.test(activityText(item)));
    return activity?.insertDate || "";
  };

  const firstStatusChange = activities.find((activity) => activity.newStatusCode && !/SUBMITTED/.test(String(activity.newStatusCode).toUpperCase()));
  const receivedDate = activityDate(/INITIAL[_ ]VALIDATION|VALIDATION[_ ]COMPLETED|APPLICATION[_ ]RECEIVED|\bRECEIVED\b/) || firstStatusChange?.insertDate || (!isSubmitted ? request.updatedDate : "");
  const processDate = activityDate(/UNDER[_ ]PROCESS|IN[_ ]PROGRESS|PROCESSING|SOCIAL[_ ]RESEARCH|AUDIT|COMMITTEE|FINANCE/) || receivedDate;
  const actionRequiredDate = requirements[0]?.requestedDate || activityDate(/ACTION[_ ]REQUIRED|INFORMATION[_ ]REQUESTED|DOCUMENT[_ ]REQUESTED|WAITING[_ ]APPLICANT/, true);
  const approvalDate = activityDate(/APPROV|اعتماد|موافق/, true);
  const rejectionDate = activityDate(/REJECT|رفض|مرفوض/, true);
  const completionDate = activityDate(/COMPLET|CLOS|ARCHIV|اكتمال|مكتمل|إغلاق|أرشفة/, true);
  const hasReceived = !isSubmitted || Boolean(receivedDate);
  const hasUnderProcess = hasReceived && !isReceivedOnly && !isSubmitted && !isExpired;
  const hasFinalResult = isApproved || isRejected;
  const milestones = [{
    statusKey: "submitted",
    title: isArabic ? "تم تقديم الطلب" : "Submitted",
    description: isArabic ? "تم تقديم الطلب بنجاح." : "The application has been successfully submitted.",
    date: activityDate(/SUBMIT|CREATE|تقديم|إنشاء/) || request.submittedDateTime || request.submittedDate,
  }];

  if (hasReceived) milestones.push({
    statusKey: "received",
    title: isArabic ? "تم استلام الطلب" : "Received",
    description: isArabic ? "تم استلام الطلب واستكمال التحقق الأولي." : "The application has been received and the initial validation has been completed.",
    date: receivedDate,
  });
  if (hasUnderProcess) milestones.push({
    statusKey: "under-process",
    title: isArabic ? "قيد الإجراء" : "Under Process",
    description: isArabic ? "الطلب قيد المراجعة والمعالجة حالياً." : "The application is currently under review and processing.",
    date: processDate,
  });
  if (isActionRequired) milestones.push({
    statusKey: "action-required",
    title: isArabic ? "إجراء مطلوب من المتعامل" : "Action Required",
    description: isArabic ? "يرجى تقديم المعلومات أو المستندات الإضافية المطلوبة." : "Additional information or documents are required from the applicant.",
    date: actionRequiredDate || request.updatedDate,
  });
  if (isExpired) milestones.push({
    statusKey: "expired",
    title: isArabic ? "منتهي الصلاحية" : "Expired",
    description: isArabic ? "انتهت صلاحية الطلب." : "The application has expired.",
    date: activityDate(/EXPIRED|EXPIRY|انتهاء|منتهي/, true) || request.updatedDate,
  });
  if (hasFinalResult) milestones.push({
    statusKey: isRejected ? "rejected" : "approved",
    title: isRejected ? (isArabic ? "مرفوض" : "Rejected") : (isArabic ? "تمت الموافقة" : "Approved"),
    description: isRejected
      ? (isArabic ? "تم رفض الطلب." : "The application has been rejected.")
      : (isArabic ? "تمت الموافقة على الطلب." : "The application has been approved."),
    date: (isRejected ? rejectionDate : approvalDate) || completionDate || request.updatedDate,
  });

  const paymentText = `${request.paymentMethod || ""} ${request.paymentStatus || ""}`.toUpperCase();
  const isChequePayment = /CHEQUE|CHECK/.test(paymentText);
  const paymentFinished = /COLLECT|COMPLET|PAID|EXECUT|SUCCESS/.test(paymentText) || Boolean(request.paymentDate) || isCompleted;
  const hasPayment = isApproved && Number(request.approvedAmount) > 0 && (isChequePayment ? Boolean(paymentText.trim()) : paymentFinished);
  if (hasPayment) {
    const isBankTransfer = /BANK|TRANSFER/.test(paymentText);
    milestones.push({
      statusKey: isChequePayment ? "cheque-ready" : "payment",
      title: isArabic ? "الصرف" : "Payment",
      description: isChequePayment
        ? (isArabic ? "الشيك جاهز" : "Cheque Ready")
        : (isArabic ? "جارٍ استكمال إجراءات الصرف." : "The payment is being processed."),
      date: completionDate || approvalDate || request.updatedDate,
    });
    if (paymentFinished) milestones.push({
      statusKey: isChequePayment ? "cheque-collected" : isBankTransfer ? "bank-transfer-completed" : "completed",
      title: isArabic ? "مكتمل" : "Completed",
      description: isChequePayment
        ? (isArabic ? "تم استلام الشيك" : "Cheque Collected")
        : isBankTransfer
          ? (isArabic ? "تم التحويل البنكي" : "Bank Transfer Completed")
          : (isArabic ? "اكتملت عملية الصرف." : "The payment has been completed."),
      date: request.paymentDate || completionDate || request.updatedDate,
    });
  } else if (isCompleted && isApproved) {
    milestones.push({
      statusKey: "completed",
      title: isArabic ? "مكتمل" : "Completed",
      description: isArabic ? "اكتمل الطلب." : "The application has been completed.",
      date: completionDate || request.updatedDate,
    });
  }

  const journeyComplete = isRejected || isCompleted || isExpired || paymentFinished;
  return milestones.map((milestone, index) => ({
    ...milestone,
    state: index === milestones.length - 1 && !journeyComplete ? "active" : "completed",
  }));
};

export function AidTrackingResult({ request, language = "en" }) {
  const isArabic = language === "ar";
  if (!request) return null;
  const customerRequestNumber = getCustomerRequestNumber(request);

  return <section className="track-result-page" aria-live="polite">
    <div className="track-result-card">
      <div className="result-top">
        <div><span className="small-title">{isArabic ? "رقم الطلب" : "Request Number"}</span><h2>{customerRequestNumber || (isArabic ? "غير متاح" : "Not available")}</h2></div>
        <div className={`approved-status track-aid-design-status customer-status-${getCustomerStatusKey(request)}`}>{getCustomerStatus(request, isArabic)}</div>
      </div>
      <div className="track-info-grid track-aid-summary-grid">
        <div className="track-info-box"><span>{isArabic ? "اسم مقدم الطلب" : "Applicant Name"}</span><strong>{(isArabic ? request.applicantName_ar : null) || request.applicantName || (isArabic ? "غير متاح" : "Not available")}</strong></div>
        <div className="track-info-box"><span>{isArabic ? "الفئة الفرعية" : "Request Type"}</span><strong>{(isArabic ? (request.subcategory_ar || request.subCategory_ar || request.requestType_ar) : null) || request.subcategory || request.subCategory || request.requestType || (isArabic ? "طلب مساعدة" : "Aid Request")}</strong></div>
        <div className="track-info-box"><span>{isArabic ? "تاريخ التقديم" : "Submitted Date"}</span><strong>{request.submittedDate || (isArabic ? "غير متاح" : "Not available")}</strong></div>
        <div className="track-info-box"><span>{isArabic ? "المبلغ المطلوب" : "Requested Amount"}</span><strong>{formatAmount(request.requestedAmount, language)}</strong></div>
        <div className="track-info-box"><span>{isArabic ? "المبلغ المعتمد" : "Approved Amount"}</span><strong>{Number(request.approvedAmount) > 0 ? formatAmount(request.approvedAmount, language) : (isArabic ? "بانتظار الموافقة" : "Waiting for approval")}</strong></div>
      </div>
      <div className="track-time-line-wrap"><h3>{isArabic ? "الجدول الزمني للطلب" : "Application Timeline"}</h3><div className="timeline-wrap">
        {buildTimeline(request, isArabic).map((item) => <div className={`timeline-item ${item.state} customer-status-${item.statusKey}`} key={item.title}>
          <div className="timeline-dot"></div><div className="timeline-content"><h4>{item.title}</h4>{item.description ? <p>{item.description}</p> : null}{item.date ? <span>{item.date}</span> : null}</div>
        </div>)}
      </div></div>
      <div className="support-message-box"><div><h4>{isArabic ? "هل تحتاج إلى دعم إضافي؟" : "Need Additional Support?"}</h4><p>{isArabic ? "يرجى التواصل مع فريق الدعم لدينا إذا كنت بحاجة إلى مزيد من المساعدة." : "Please contact our support team if you need further assistance."}</p></div><Link className="support-btn" to="/contact-us">{isArabic ? "تواصل مع الدعم" : "Contact Support"}</Link></div>
    </div>
  </section>;
}

export default function TrackAidRequestStatus({ language = "en" }) {
  const isArabic = language === "ar";
  const interactions = useHomeInteractions();
  const [requestNo, setRequestNo] = useState("");
  const [request, setRequest] = useState(null);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.title = isArabic ? "تتبع حالة طلب المساعدة | مؤسسة تراحم الخيرية" : "Track Aid Request Status | Tarahum Charity Foundation";
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
      document.body.classList.remove("arabic-layout-page");
    };
  }, [isArabic]);

  const trackRequest = async (event) => {
    event.preventDefault();
    const value = requestNo.trim();
    if (!value) { setRequest(null); setMessage(isArabic ? "يرجى إدخال رقم الطلب." : "Please enter your request number."); return; }

    setIsLoading(true); setMessage(""); setRequest(null);
    try {
      const token = getStoredToken();
      const response = await fetch(getApiUrl(`/api/GetAidRequestStatus.ashx?requestno=${encodeURIComponent(value)}`), {
        credentials: "include", headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(result.message || (isArabic ? "تعذر استرجاع حالة الطلب." : "Unable to retrieve the request status."));
      let dashboardRequests = [];
      try {
        const dashboardResponse = await fetch(getApiUrl("/api/BeneficiaryDashboard.ashx"), {
          credentials: "include", headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const dashboardResult = await dashboardResponse.json().catch(() => ({}));
        if (dashboardResponse.ok && dashboardResult.success) dashboardRequests = dashboardResult.requests || [];
      } catch { /* The tracking result remains available if the dashboard cross-check fails. */ }
      setRequest(syncExpiredStatusFromDashboard(result.request, dashboardRequests));
    } catch (error) {
      setMessage(error.message || (isArabic ? "تعذر استرجاع حالة الطلب." : "Unable to retrieve the request status."));
    } finally { setIsLoading(false); }
  };

  return <>
    <Preloader />{isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}
    <section className="kid-d-wrp pb-0"><div className="kid-container"><div className="kid-hero"><h1>{isArabic ? "تتبع حالة طلب المساعدة" : "Track AID Request Status"}</h1><ul className="kid-breadcrumb"><li><Link to="/">{isArabic ? "الرئيسية" : "home"}</Link></li><li>/</li><li>{isArabic ? "تتبع حالة طلب المساعدة" : "Track AID Request Status"}</li></ul></div></div></section>
    <main className={`page-service-single track-aid-page${isArabic ? " arabic-track-aid-page" : ""}`} dir={isArabic ? "rtl" : undefined}><div className="container"><div className="track-wrap">
      <p className="title">{isArabic ? "يرجى إدخال رقم الطلب للاطلاع على الحالة الحالية لطلب المساعدة المقدم." : "Please enter your request number to view the current status of your submitted aid request."}</p>
      <form className="track-box" onSubmit={trackRequest}>
        <label htmlFor="aidRequestNumber"><span>*</span> {isArabic ? "رقم الطلب" : "Request Number"}</label>
        <input id="aidRequestNumber" type="text" placeholder={isArabic ? "أدخل رقم الطلب" : "Enter your request number"} value={requestNo} onChange={(event) => setRequestNo(event.target.value)} autoComplete="off" />
        <button className="btn-track" type="submit" disabled={isLoading}>{isLoading ? (isArabic ? "جارٍ التحقق..." : "Checking...") : (isArabic ? "تتبع حالة الطلب" : "Track Request Status")}</button>
      </form>
      {message ? <p className="track-aid-message" role="alert">{message}</p> : null}
      <AidTrackingResult request={request} language={language} />
    </div></div></main>
    {isArabic ? <ArabicFooter /> : <Footer />}<FloatingWidgets {...interactions} /><HomeModals {...interactions} />
  </>;
}
